import React, { useState, useEffect } from "react";
import { Layout, Button } from "antd";
import { Api } from "@model";
import { useNavigate, useParams } from "react-router-dom";
import { useIsCompact } from "../../hooks/useIsCompact";

const { Header } = Layout;

export type CharacterMessagesPanelProps = {
  characterID: string;
  /** When true, fits inside admin layout (e.g. character edit tab). */
  embedded?: boolean;
};

export function CharacterMessagesPanel({
  characterID,
  embedded,
}: CharacterMessagesPanelProps) {
  const [messages, setMessages] = useState<any[]>([]);
  const [, setLoading] = useState(true);
  const [userInfoList, setUserInfoList] = useState<any[]>([]);
  const [page] = useState(1);

  useEffect(() => {
    const fetchMessages = async () => {
      setLoading(true);
      try {
        const response = await Api.get(
          `/api/admin/panel/chats/grouped?characterID=${characterID}&page=${page}`,
        );
        if (response.data && response.data.data) {
          const data = response.data.data;
          setUserInfoList(
            data.map((item: any) => {
              const user = item.userDetails[0];
              return {
                userID: item._id,
                name: user ? user.name : "Unknown",
                email: user ? user.email : "Unknown",
                messages: item.messages || [],
              };
            }),
          );
        }
      } finally {
        setLoading(false);
      }
    };

    if (characterID) {
      fetchMessages();
    }
  }, [characterID, page]);

  const handleUserClick = (userID: string) => {
    const selectedUser = userInfoList.find((user) => user.userID === userID);
    if (selectedUser && selectedUser.messages) {
      const allMessages = selectedUser.messages.sort(
        (a: any, b: any) =>
          new Date(a.sendDate).getTime() - new Date(b.sendDate).getTime(),
      );
      setMessages(allMessages);
    } else {
      setMessages([]);
    }
  };

  const isCompact = useIsCompact();
  const innerHeight = embedded
    ? "min(70vh, 640px)"
    : "calc(100vh - 64px)";

  return (
    <div
      style={{
        display: "flex",
        flexDirection: isCompact ? "column" : "row",
        minHeight: isCompact ? undefined : innerHeight,
        background: "transparent",
        gap: isCompact ? 8 : 0,
      }}
    >
      <div
        style={{
          width: isCompact ? "100%" : 300,
          flex: isCompact ? "0 0 auto" : "0 0 300px",
          background: "#fff",
          padding: 12,
          boxShadow: isCompact ? "none" : "2px 0 5px rgba(0, 0, 0, 0.1)",
          border: isCompact ? "1px solid #f0f0f0" : undefined,
          borderRadius: isCompact ? 8 : 0,
          overflowY: "auto",
          maxHeight: isCompact ? 220 : innerHeight,
          height: isCompact ? undefined : innerHeight,
        }}
      >
        <div>
          {userInfoList.map((user, index) => (
            <div
              key={index}
              style={{
                padding: "10px",
                marginBottom: "5px",
                background: "#f5f5f5",
                borderRadius: "5px",
                cursor: "pointer",
              }}
              onClick={() => handleUserClick(user.userID)}
            >
              <div>{user.name}</div>
              <div style={{ overflowWrap: "anywhere" }}>{user.email}</div>
            </div>
          ))}
        </div>
      </div>
      <div
        style={{
          flex: 1,
          minWidth: 0,
          minHeight: isCompact ? 320 : undefined,
          padding: 12,
          display: "flex",
          flexDirection: "column",
          height: isCompact ? 420 : innerHeight,
          background: "#f5f5f5",
          overflow: "hidden",
          borderRadius: isCompact ? 8 : 0,
        }}
      >
        <div style={{ flexGrow: 1, overflowY: "auto", padding: isCompact ? 4 : 16 }}>
          {messages.map((message, index) => (
            <div
              key={index}
              style={{
                display: "flex",
                justifyContent:
                  message.role === "user" ? "flex-end" : "flex-start",
                marginBottom: "16px",
              }}
            >
              <div
                style={{
                  maxWidth: isCompact ? "88%" : "70%",
                  padding: "10px 15px",
                  borderRadius: "20px",
                  backgroundColor:
                    message.role === "user" ? "#1890ff" : "#f0f0f0",
                  color: message.role === "user" ? "white" : "black",
                  textAlign: message.role === "user" ? "right" : "left",
                  boxShadow: "0 2px 5px rgba(0, 0, 0, 0.1)",
                  wordWrap: "break-word",
                }}
              >
                <p style={{ margin: 0 }}>{message.message}</p>
                <small
                  style={{
                    display: "block",
                    marginTop: 8,
                    color: message.role === "user" ? "#d1eaff" : "#888",
                  }}
                >
                  {new Date(message.sendDate).toLocaleString()}
                </small>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const CharacterMessagesPage = () => {
  const { characterID } = useParams<{ characterID: string }>();
  const navigate = useNavigate();

  return (
    <Layout className="no-background" style={{ height: "100vh" }}>
      <Header
        style={{
          background: "#002140",
          padding: "0 10px",
          color: "white",
          fontSize: "18px",
          display: "flex",
          alignItems: "center",
        }}
      >
        <Button
          type="text"
          onClick={() => navigate(-1)}
          style={{ color: "white", marginRight: "10px" }}
        >
          Back
        </Button>
      </Header>
      {characterID ? (
        <CharacterMessagesPanel characterID={characterID} />
      ) : null}
    </Layout>
  );
};

export default CharacterMessagesPage;
